import React, { useEffect, useState } from "react";
import {
  login,
  forgotPassword,
  confirmForgotPassword,
} from "../apiService";

// Form content only. The modal shell (overlay, orange header, close button)
// lives in Referral.tsx, so there is a single modal on the page. This
// component reports which screen it's on via onModeChange so the header
// title in Referral.tsx can follow. Behaviour is unchanged: the user stays
// on /referral after login and onLoginSuccess() unlocks the form.

const ReferralLoginModal = ({ onLoginSuccess, onModeChange }) => {
  const [mode, setMode] = useState("login");

  // Login fields
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Forgot password fields
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [confirmSuccess, setConfirmSuccess] = useState("");
  const [resendCountdown, setResendCountdown] = useState(0);

  // Tell the parent which screen we're on so it can update the header title.
  useEffect(() => {
    if (typeof onModeChange === "function") onModeChange(mode);
  }, [mode]);

  useEffect(() => {
    let timer;
    if (resendCountdown > 0) {
      timer = setTimeout(() => setResendCountdown((c) => c - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCountdown]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // navigate is omitted and redirectTo is explicitly null so login()
      // does NOT bounce the user to /profile — they stay on /referral,
      // whether they arrived by QR code or any other link.
      const result = await login(username, password, undefined, null);

      if (result.success) {
        onLoginSuccess();
      } else {
        setError(result.detail || result.message || "Invalid credentials.");
      }
    } catch (err) {
      console.error("Referral login error:", err);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!email && !phoneNumber) {
      setError("Please enter your email or phone number.");
      return;
    }

    try {
      const result = await forgotPassword(email, phoneNumber);
      if (result.success) {
        setMode("confirm");
        setResendCountdown(60);
      } else {
        setError(result.message);
      }
    } catch (err) {
      console.error("Referral forgot-password error:", err);
      setError("An unexpected error occurred. Please try again.");
    }
  };

  const handleConfirmForgotPassword = async (e) => {
    e.preventDefault();
    setConfirmError("");
    setConfirmSuccess("");

    if (newPassword !== confirmNewPassword) {
      setConfirmError("New password and confirmation do not match.");
      return;
    }

    try {
      const result = await confirmForgotPassword({
        email,
        mobileNumber: phoneNumber,
        otp,
        newPassword,
      });

      if (result.success) {
        setConfirmSuccess("Password reset successfully. You can now log in.");
        setTimeout(() => {
          setMode("login");
          setConfirmSuccess("");
        }, 2500);
      } else {
        setConfirmError(result.message);
      }
    } catch (err) {
      console.error("Referral reset error:", err);
      setConfirmError("An error occurred. Please try again.");
    }
  };

  const handleResendOTP = async () => {
    setConfirmError("");
    setConfirmSuccess("");

    if (!email && !phoneNumber) {
      setConfirmError("Please enter your email or phone number first.");
      return;
    }

    try {
      const result = await forgotPassword(email, phoneNumber);
      if (result.success) {
        setConfirmSuccess("OTP resent successfully.");
        setResendCountdown(60);
      } else {
        setConfirmError(result.message);
      }
    } catch (err) {
      setConfirmError("Failed to resend OTP. Please try again.");
    }
  };

  const backToLogin = () => {
    setError("");
    setMode("login");
  };

  // Header is provided by the Modal in Referral.tsx, so this just wraps the body.
  const renderShell = (title, body) => (
    <div className="referral-login-modal">{body}</div>
  );

  // Password input with Show/Hide button (input-group)
  const passwordField = ({ id, label, value, onChange, show, setShow, extra = {} }) => (
    <div className="mb-3">
      <label htmlFor={id} className="form-label">
        {label}
      </label>
      <div className="input-group">
        <input
          id={id}
          type={show ? "text" : "password"}
          className="form-control"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required
          {...extra}
        />
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => setShow((v) => !v)}
        >
          {show ? "Hide" : "Show"}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {mode === "login" &&
        renderShell(
          "Login",
          <form onSubmit={handleLogin}>
            <p className="referral-subtitle">
              Your details are pulled straight from your account, so log in
              below — the referral form unlocks right here once you're in.
            </p>

            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            <div className="mb-3">
              <label htmlFor="referral-username" className="form-label">
                Email or Mobile Number
              </label>
              <input
                id="referral-username"
                type="text"
                className="form-control"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter email or phone number"
                autoComplete="username"
                required
              />
            </div>

            {passwordField({
              id: "referral-password",
              label: "Password",
              value: password,
              onChange: setPassword,
              show: showPassword,
              setShow: setShowPassword,
              extra: { autoComplete: "current-password" },
            })}

            <button
              type="submit"
              className="ftthsa btn-secondary w-100"
              disabled={loading}
            >
              {loading ? "Logging in..." : "Login"}
            </button>

            <div className="text-center mt-3">
              <button
                type="button"
                className="btn btn-link"
                onClick={() => {
                  setError("");
                  setMode("forgot");
                }}
              >
                Forgot Password?
              </button>
            </div>
          </form>
        )}

      {mode === "forgot" &&
        renderShell(
          "Forgot Password",
          <form onSubmit={handleForgotPasswordSubmit}>
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            <div className="mb-3">
              <label htmlFor="referral-email" className="form-label">
                Enter Registered Email
              </label>
              <input
                id="referral-email"
                type="email"
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
              />
            </div>

            <div className="mb-3">
              <label htmlFor="referral-phone" className="form-label">
                Enter Registered Phone Number
              </label>
              <input
                id="referral-phone"
                type="text"
                className="form-control"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                autoComplete="tel"
              />
            </div>

            <button type="submit" className="ftthsa btn-secondary w-100">
              Submit
            </button>

            <div className="text-center mt-3">
              <button
                type="button"
                className="btn btn-link"
                onClick={backToLogin}
              >
                Back to Login
              </button>
            </div>
          </form>
        )}

      {mode === "confirm" &&
        renderShell(
          "Confirm Password Reset",
          <form onSubmit={handleConfirmForgotPassword}>
            {confirmError && (
              <div className="alert alert-danger" role="alert">
                {confirmError}
              </div>
            )}
            {confirmSuccess && (
              <div className="alert alert-success" role="alert">
                {confirmSuccess}
              </div>
            )}

            <div className="mb-3">
              <label htmlFor="referral-otp" className="form-label">
                OTP
              </label>
              <input
                id="referral-otp"
                type="text"
                className="form-control"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                autoComplete="one-time-code"
                required
                minLength={4}
                maxLength={4}
              />
            </div>

            {passwordField({
              id: "referral-new-password",
              label: "New Password",
              value: newPassword,
              onChange: setNewPassword,
              show: showNewPassword,
              setShow: setShowNewPassword,
              extra: { autoComplete: "new-password", minLength: 6, maxLength: 20 },
            })}

            {passwordField({
              id: "referral-confirm-password",
              label: "Confirm New Password",
              value: confirmNewPassword,
              onChange: setConfirmNewPassword,
              show: showConfirmNewPassword,
              setShow: setShowConfirmNewPassword,
              extra: { autoComplete: "new-password", minLength: 6, maxLength: 20 },
            })}

            <button type="submit" className="ftthsa btn-secondary w-100">
              Reset Password
            </button>

            <button
              type="button"
              className="btn btn-link w-100 mt-2"
              onClick={handleResendOTP}
              disabled={resendCountdown > 0}
            >
              {resendCountdown > 0
                ? `Resend OTP (${resendCountdown}s)`
                : "Resend OTP"}
            </button>
          </form>
        )}

      <style>{`
        .referral-login-modal {
          width: 100%;
        }

        .referral-subtitle {
          font-size: 0.9rem;
          color: #6b7280;
          line-height: 1.5;
          margin: 0 0 1.25rem;
        }

        .referral-login-modal .form-label {
          font-weight: 500;
          margin-bottom: 0.4rem;
        }

        .referral-login-modal .form-control {
          min-height: 42px;
          border-radius: 4px;
        }

        .referral-login-modal .form-control:focus {
          border-color: #e2520f;
          box-shadow: 0 0 0 0.2rem rgba(226, 82, 15, 0.15);
        }

        .referral-login-modal .input-group .form-control {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }

        .referral-login-modal .input-group .btn {
          min-width: 70px;
          background-color: #6c757d;
          border-color: #6c757d;
          color: #ffffff;
        }

        .referral-login-modal .input-group .btn:hover {
          background-color: #5c636a;
          border-color: #565e64;
        }

        .referral-login-modal .ftthsa {
          min-height: 42px;
          border-radius: 4px;
        }

        .referral-login-modal .btn-link {
          color: #e2520f;
          text-decoration: none;
        }

        .referral-login-modal .btn-link:hover {
          color: #c7440b;
          text-decoration: underline;
        }

        .referral-login-modal .alert {
          margin-bottom: 1rem;
        }
      `}</style>
    </>
  );
};

export default ReferralLoginModal;